import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { scribeLayoutTourRelaunchRequestedAtom } from '@/atoms/sessionPageV2Atoms';
import { trackSessionLayoutSwitch } from '@/models/analytics/features/track-session-layout-switch';
import {
  LABS_FEATURE,
  useIsAutoEnrolledSessionLayoutV2,
  useLabsFeatureOptIn,
} from './labs-overrides';
import { SESSION_LAYOUT_V2_RELEASE_FLAGS } from './registry';
import { useSetLabsFeatureOptIn } from './use-set-labs-feature-opt-in';

/**
 * State and persistence for the compact Session Layout V2 toggle in the avatar
 * menu. It deliberately mirrors the Labs card's two release gates: users
 * outside the rollout must never see a switch that cannot change their layout.
 *
 * Auto-enrolled users are intentionally excluded. Their backend-owned enrolment
 * stamp takes precedence over Labs overrides, so exposing a switch would imply
 * an opt-out that cannot take effect.
 *
 * TODO(SCR-7277): Remove this temporary manual override when Session Page V2
 * becomes the default experience for agent-launched sessions.
 */
export const useSessionLayoutV2MenuToggle = () => {
  const isNewSessionListEnabled = useBooleanFlagValue(
    SESSION_LAYOUT_V2_RELEASE_FLAGS[0],
    false
  );
  const isV2ReleaseEnabled = useBooleanFlagValue(
    SESSION_LAYOUT_V2_RELEASE_FLAGS[1],
    false
  );
  const isAutoEnrolled = useIsAutoEnrolledSessionLayoutV2();
  const optIn = useLabsFeatureOptIn(LABS_FEATURE.sessionLayoutV2);
  const requestTourRelaunch = useSetAtom(scribeLayoutTourRelaunchRequestedAtom);
  const { setOptIn, isPending } = useSetLabsFeatureOptIn({
    // Mutation-level tracking survives the menu (or the legacy session layout)
    // unmounting while the opt-in request is still in flight.
    onOptInPersisted: (overrides) => {
      const enabled = overrides[LABS_FEATURE.sessionLayoutV2];
      if (enabled === undefined) return;
      trackSessionLayoutSwitch(
        enabled ? 'account_menu_enabled' : 'account_menu_disabled'
      );
      if (enabled) requestTourRelaunch(true);
    },
  });

  const setEnabled = useCallback(
    (enabled: boolean) => setOptIn(LABS_FEATURE.sessionLayoutV2, enabled),
    [setOptIn]
  );

  return {
    isVisible: isV2ReleaseEnabled && isNewSessionListEnabled && !isAutoEnrolled,
    isEnabled: optIn === true,
    isPending,
    setEnabled,
  };
};
